// Design System TextField Component
// Unified text input component with all variants and features

import React, { useState, forwardRef } from 'react';
import { 
  TextField as MuiTextField, 
  InputAdornment, 
  Box,
  alpha,
  styled
} from '@mui/material';
import PropTypes from 'prop-types';
import { colorTokens, spacingTokens, borderTokens, transitionTokens } from '../../tokens';

// Styled wrapper for consistent spacing
const TextFieldWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  marginBottom: spacingTokens.component.md,
}));

// Styled TextField with design system tokens
const StyledTextField = styled(MuiTextField, {
  shouldForwardProp: (prop) => !['magical', 'floating', 'hasError'].includes(prop),
})(({ theme, magical, floating, hasError }) => {
  const mode = theme.palette.mode;
  const colors = mode === 'dark' ? colorTokens.dark : colorTokens.light;
  
  return {
    width: '100%',
    
    // Floating animation
    ...(floating && {
      animation: 'float 4s ease-in-out infinite',
      '@keyframes float': {
        '0%, 100%': { transform: 'translateY(0)' },
        '50%': { transform: 'translateY(-2px)' },
      },
    }),
    
    // Input root styles
    '& .MuiOutlinedInput-root': {
      minHeight: 48,
      borderRadius: borderTokens.lg,
      transition: transitionTokens.standard,
      backgroundColor: alpha(colors.background.paper, mode === 'dark' ? 0.5 : 0.95),
      
      // Notched outline
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: alpha(colors.divider, mode === 'dark' ? 0.4 : 0.5),
        borderWidth: 1,
        transition: transitionTokens.fast,
      },
      
      // Hover state
      '&:hover:not(.Mui-focused):not(.Mui-error)': {
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: colors.primary.main,
        },
      },
      
      // Focused state
      '&.Mui-focused': {
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: colors.primary.main,
          borderWidth: 2,
        },
      },
      
      // Error state
      '&.Mui-error': {
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: `${colors.error.main} !important`,
          borderWidth: 2,
        },
      },
      
      // Input element
      '& .MuiInputBase-input': {
        padding: `${spacingTokens.component.sm} ${spacingTokens.component.md}`,
        fontSize: '0.875rem',
        color: colors.text.primary,
      },
      
      // Adornments
      '& .MuiInputAdornment-root': {
        '& svg': {
          color: colors.text.secondary,
          fontSize: '1.25rem',
        },
      },
    },
    
    // Label styles
    '& .MuiInputLabel-root': {
      color: alpha(colors.text.primary, 0.7),
      fontWeight: 500,
      
      ...(magical && {
        transform: 'translate(14px, -9px) scale(0.75)',
        '&.MuiInputLabel-shrink': {
          transform: 'translate(14px, -9px) scale(0.75)',
        },
      }),
      
      '&.Mui-focused:not(.Mui-error)': {
        color: colors.primary.main,
        fontWeight: 600,
      },
      
      '&.Mui-error': {
        color: `${colors.error.main} !important`,
        fontWeight: 600,
      },
    },
    
    // Helper text
    '& .MuiFormHelperText-root': {
      marginLeft: spacingTokens.component.sm,
      marginTop: spacingTokens.component.xs,
      fontSize: '0.75rem',
      color: colors.text.secondary,
      
      '&.Mui-error': {
        color: colors.error.main,
      },
    },
  };
});

/**
 * TextField - Unified text input component for the design system
 * 
 * Features:
 * - All MUI TextField props supported
 * - Magical mode with always-shrunk label
 * - Floating animation
 * - Start/end adornments with proper styling
 * - Design system tokens integration
 * - Consistent error handling
 * - Field strength indicator for passwords
 * 
 * @example
 * // Basic usage
 * <TextField label="Name" value={name} onChange={setName} />
 * 
 * // With adornments
 * <TextField 
 *   label="Email" 
 *   startAdornment={<EmailIcon />}
 *   type="email"
 * />
 * 
 * // Password with strength
 * <TextField 
 *   label="Password" 
 *   type="password"
 *   fieldStrength={passwordStrength}
 * />
 */
const TextField = forwardRef(({
  label,
  helperText,
  error,
  startAdornment,
  endAdornment,
  magical = true, // Keep backward compatibility
  floating = false,
  fullWidth = true,
  variant = 'outlined',
  size = 'medium',
  fieldStrength,
  showCompletionStatus = false,
  completed,
  onFocus,
  onBlur,
  InputProps = {},
  InputLabelProps = {},
  ...props
}, ref) => {
  const [focused, setFocused] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  
  // Handle focus
  const handleFocus = (e) => {
    setFocused(true);
    onFocus?.(e);
  };
  
  // Handle blur
  const handleBlur = (e) => {
    setFocused(false);
    setHasInteracted(true);
    onBlur?.(e);
  };
  
  // Determine completion status
  const isCompleted = Boolean(
    completed || 
    (props.value && hasInteracted && !error && showCompletionStatus)
  );
  
  // Process start adornment
  const processedStartAdornment = startAdornment && (
    <InputAdornment position="start">
      {React.isValidElement(startAdornment) 
        ? React.cloneElement(startAdornment, {
            sx: {
              color: error ? 'error.main' : isCompleted ? 'success.main' : 'text.secondary',
              fontSize: '1.25rem',
              transition: transitionTokens.fast,
              ...startAdornment.props?.sx
            }
          })
        : startAdornment}
    </InputAdornment>
  );
  
  // Process end adornment with status
  const statusIcon = isCompleted && showCompletionStatus && (
    <Box component="span" sx={{ color: 'success.main', fontSize: '1.2rem' }}>✓</Box>
  );
  
  const processedEndAdornment = (endAdornment || statusIcon) && (
    <InputAdornment position="end">
      {statusIcon}
      {React.isValidElement(endAdornment)
        ? React.cloneElement(endAdornment, {
            sx: {
              color: error ? 'error.main' : isCompleted ? 'success.main' : 'text.secondary',
              fontSize: '1.25rem',
              transition: transitionTokens.fast,
              ...endAdornment.props?.sx
            }
          })
        : endAdornment}
    </InputAdornment>
  );
  
  // Field strength indicator
  const strengthIndicator = fieldStrength != null && !error && (
    <Box sx={{
      mt: 0.5,
      height: 3,
      width: '100%',
      backgroundColor: theme => alpha(theme.palette.divider, 0.2),
      borderRadius: 3,
      overflow: 'hidden'
    }}>
      <Box sx={{
        height: '100%',
        width: `${Math.min(100, fieldStrength * 25)}%`,
        backgroundColor: fieldStrength <= 1 ? 'error.main' :
                        fieldStrength <= 2 ? 'warning.main' :
                        fieldStrength <= 3 ? 'info.main' :
                        'success.main',
        transition: transitionTokens.standard,
      }} />
    </Box>
  );
  
  return (
    <TextFieldWrapper>
      <StyledTextField
        ref={ref}
        label={label}
        error={Boolean(error)}
        helperText={helperText}
        variant={variant}
        fullWidth={fullWidth}
        size={size}
        magical={magical}
        floating={floating}
        hasError={Boolean(error)}
        InputLabelProps={{
          shrink: magical || props.placeholder ? true : undefined,
          ...InputLabelProps,
        }}
        InputProps={{
          startAdornment: processedStartAdornment,
          endAdornment: processedEndAdornment,
          ...InputProps,
        }}
        onFocus={handleFocus}
        onBlur={handleBlur}
        {...props}
      />
      {strengthIndicator}
    </TextFieldWrapper>
  );
});

TextField.propTypes = {
  label: PropTypes.string,
  helperText: PropTypes.node,
  error: PropTypes.bool,
  startAdornment: PropTypes.node,
  endAdornment: PropTypes.node,
  magical: PropTypes.bool,
  floating: PropTypes.bool,
  fullWidth: PropTypes.bool,
  variant: PropTypes.oneOf(['outlined', 'filled', 'standard']),
  size: PropTypes.oneOf(['small', 'medium']),
  fieldStrength: PropTypes.number,
  showCompletionStatus: PropTypes.bool,
  completed: PropTypes.bool,
  // All other MUI TextField props are supported
};

TextField.displayName = 'TextField';

export default TextField;